import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, stageWorldAt } from '../shaders/safeZone.glsl.js';
import { genererCanauxRuban, MATCAP_GAIN, CHROME_BOX } from './lib/matcap.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_PRESETS } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Les tubes ont leur propre lumière (matcap).
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'pipes',
  // le brief dit 'objects' ; la famille de l'orchestrateur est 'object'
  family: 'object',
  danceability: 0.84,
  energyLevel: 0.78,
  bpmMin: 110,
  bpmMax: 160,
  movement: 0.75,
  tonality: 0.55,
  enabled: true
};

// PIPES : hommage néon à l'écran de veille des années 90. Des tuyaux
// poussent dans une grille 3D invisible, segment par segment, en tournant à
// angle droit, jusqu'à remplir l'espace ; chaque segment pousse sur une
// croche, les tuyaux sont des tubes de néon, et l'espace se vide en explosion
// sur les drops. Électro, house, techno, synthwave. Règles R1 à R19, preset
// party de vdjLook (R9 : danceability 0,84).
//
// LA POUSSE (CPU, testable sans GPU : PipeGrid) : une grille d'occupation
// (16 x 10 x 10, tier 0 : 12 x 8 x 8). À chaque pas (une croche ; une double
// croche en montée), chaque tuyau vivant avance d'une cellule : tout droit
// (60 %) si la cellule est libre, sinon un virage à 90° vers une cellule
// libre au hasard ; bloqué, il s'arrête et un nouveau naît ailleurs. La
// décision a UN PAS D'AVANCE : quand le segment A -> B naît, la cellule
// suivante C est déjà choisie et réservée, si bien que le virage en B (et
// son joint) est connu avant que la tête y arrive. Un tuyau n'occupe que ses
// cellules, et chaque pièce (cylindre de centre à centre, sphère au centre,
// coude de rayon une demi-cellule) reste dans les siennes : aucun tuyau ne
// peut en traverser un autre, par construction.
//
// LE RENDU : deux maillages instanciés (segments : cylindres ; joints :
// sphères OU coudes en quart de tore, au choix de la phrase) dont les
// données vivent dans des textures flottantes, réécrites seulement quand un
// segment s'ajoute. Le segment qui pousse s'allonge dans le vertex shader
// d'après une horloge de pas continue : la tête avance sans saut. Au coude,
// les deux segments sont raccourcis d'une demi-cellule et l'arc pousse lui
// aussi. Néon : la matcap chrome de lib/matcap.js teintée très saturée
// (R4), zones sombres, reflet blanc net, coeur plus lumineux face à la
// caméra (un tube éclairé de l'intérieur) ; tête plus lumineuse et une lueur
// dans l'air (quads additifs, halo nul au bord : R12). MSAA 4x dans une cible
// privée (R5).
//
// LE RYTHME (preset party)
//   CROCHE   chaque tuyau avance d'une cellule
//   TEMPS    les têtes pulsent ; séparation RGB (vdjBeatChroma)
//   MESURE   un nouveau tuyau naît, d'une nouvelle couleur (3 à 5 vivants)
//   PHRASE   coupe : nouvel angle de vue, nouveau style de joint
//   MONTÉE   double croche, jusqu'à 8 tuyaux
//   DROP     explosion : chaque pièce part selon sa normale (vers l'extérieur
//            de la structure) en tournant, balistique sur 2 temps, flash ; la
//            grille se vide, la pousse repart du centre
// À 60 % de remplissage sans drop : la structure s'éteint en 2 temps, des
// segments les plus anciens aux plus récents, puis la pousse recommence.
//
// LA PLACE (R6, R19) : la grille tourne autour de la verticale (un tour en
// 32 temps), centrée sur le CENTRE DE COMPOSITION (stageWorldAt). Pour chaque
// angle de vue, la distance de la caméra est la plus petite qui garde le
// cylindre balayé par la grille (tube compris) dans le cadre (marge 5 %) et
// hors des cinq zones d'interface, à tous les angles de la rotation.

const TIER = {
  0: { grid: [12, 8, 8], pipes: 3, build: 5, radial: 10, segs: 800, joints: 512 },
  1: { grid: [16, 10, 10], pipes: 4, build: 8, radial: 12, segs: 1600, joints: 1024 },
  2: { grid: [16, 10, 10], pipes: 5, build: 8, radial: 16, segs: 1600, joints: 1024 }
};
const TUBE_R = 0.22;                     // rayon du tube, en cellules
const ELBOW_R = 0.5;                     // rayon de courbure du coude : une demi-cellule
const JOINT_R = 0.235;                   // la sphère de joint couvre le coin extérieur
const STRAIGHT_P = 0.60;
const FILL_DISSOLVE = 0.60;
const EXPLODE_BEATS = 2, DISSOLVE_BEATS = 2;
const TURN_BEATS = 32;                   // un tour de la structure
const NO_BPM = 124;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const FRAME_MARGIN = 0.10;
const FOV = 38;
// les vues (élévation, décalage d'azimut), une par phrase : en coupe
export const VIEWS = [[24, 0], [38, 20], [14, -25], [30, 45]];
// les couleurs des tuyaux : roue néon, plus l'accent de la pochette
const NEON = [0xff2a6d, 0x05d9e8, 0xfcee0c, 0x9d4edd, 0x39ff14, 0xff8c00, 0x00f5d4, 0xff3cf0];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}
export function rng(seed) { let s = (seed >>> 0) || 1; return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }

// ------------------------------------------------------------ la pousse (CPU)
// les six directions : code = axe x 2 + (sens positif ? 1 : 0)
export const DIRS = [[-1, 0, 0], [1, 0, 0], [0, -1, 0], [0, 1, 0], [0, 0, -1], [0, 0, 1]];
const dirCode = (d) => d[0] ? (d[0] > 0 ? 1 : 0) : d[1] ? (d[1] > 0 ? 3 : 2) : (d[2] > 0 ? 5 : 4);

export class PipeGrid {
  constructor(dims, random) {
    this.dims = dims; this.r = random;
    this.occ = new Int16Array(dims[0] * dims[1] * dims[2]).fill(-1);   // -1 libre, sinon l'indice du tuyau
    this.pipes = []; this.segments = []; this.joints = []; this.filled = 0;
  }
  idx(c) { return (c[2] * this.dims[1] + c[1]) * this.dims[0] + c[0]; }
  inside(c) { return c[0] >= 0 && c[1] >= 0 && c[2] >= 0 && c[0] < this.dims[0] && c[1] < this.dims[1] && c[2] < this.dims[2]; }
  free(c) { return this.inside(c) && this.occ[this.idx(c)] < 0; }
  take(c, pipe) { this.occ[this.idx(c)] = pipe; this.filled++; }
  get fill() { return this.filled / this.occ.length; }
  // la prochaine cellule depuis `at` dans la direction `dir` : tout droit
  // (60 %) si libre, sinon un virage à 90° vers une voisine libre ; null : bloqué
  choose(at, dir) {
    const add = (d) => [at[0] + d[0], at[1] + d[1], at[2] + d[2]];
    const straight = add(dir);
    const turns = DIRS.filter((d) => (d[0] * dir[0] + d[1] * dir[1] + d[2] * dir[2]) === 0).filter((d) => this.free(add(d)));
    if (this.free(straight) && (this.r() < STRAIGHT_P || !turns.length)) return { cell: straight, dir };
    if (!turns.length) return null;
    const d = turns[Math.floor(this.r() * turns.length)];
    return { cell: add(d), dir: d };
  }
  // une naissance : une cellule libre (près du centre si `centre`), une
  // direction libre, et la cellule suivante déjà réservée
  spawn(step, colour, centre) {
    const [nx, ny, nz] = this.dims, cands = [];
    for (let tries = 0; tries < 400 && cands.length < 24; tries++) {
      const span = centre ? 0.35 : 1;
      const c = [0, 1, 2].map((k) => Math.floor(this.dims[k] * (0.5 + (this.r() - 0.5) * span)));
      if (!this.free(c)) continue;
      const dirs = DIRS.filter((d) => this.free([c[0] + d[0], c[1] + d[1], c[2] + d[2]]));
      if (dirs.length) cands.push({ c, dirs });
    }
    if (!cands.length) return null;
    const { c, dirs } = cands[Math.floor(this.r() * cands.length)];
    const dir = dirs[Math.floor(this.r() * dirs.length)];
    const id = this.pipes.length;
    const p = { id, colour, alive: true, cell: c, dir, next: null, born: step };
    this.take(c, id);
    p.next = { cell: [c[0] + dir[0], c[1] + dir[1], c[2] + dir[2]], dir };
    this.take(p.next.cell, id);
    // la sphère de départ
    this.joints.push({ cell: c, din: dir, dout: dir, birth: step - 0.15, kind: 'start', pipe: id, colour });
    this.pipes.push(p);
    return p;
  }
  // un pas : le segment de la tête vers la cellule réservée naît (il pousse
  // pendant le pas), et la suivante est choisie et réservée ; renvoie les
  // pièces ajoutées
  step(p, step) {
    if (!p.alive) return [];
    const from = p.cell, to = p.next.cell, dir = p.next.dir;
    const after = this.choose(to, dir);
    const turnEnd = !!(after && after.dir !== dir && dirCode(after.dir) !== dirCode(dir));
    const turnStart = !!p.turnIn;
    const seg = { from, dir, birth: step, turnStart, turnEnd, pipe: p.id, colour: p.colour };
    p.growFrom = from; p.growDir = dir; p.growBirth = step;
    this.segments.push(seg);
    const out = [seg];
    if (after) {
      this.take(after.cell, p.id);
      if (turnEnd) { const j = { cell: to, din: dir, dout: after.dir, birth: step + 1, kind: 'turn', pipe: p.id, colour: p.colour }; this.joints.push(j); out.push(j); }
      p.cell = to; p.next = after; p.turnIn = turnEnd;
    } else {
      // bloqué : une sphère au bout, le tuyau s'arrête
      const j = { cell: to, din: dir, dout: dir, birth: step + 1, kind: 'end', pipe: p.id, colour: p.colour };
      this.joints.push(j); out.push(j);
      p.cell = to; p.alive = false;
    }
    return out;
  }
}

// ------------------------------------------------------------- la caméra (R6)
const _v = new THREE.Vector3();
function forbidden(nx, ny, A) {
  const m = FRAME_MARGIN;
  if (Math.abs(ny) > 1 - m || Math.abs(nx) > 1 - m / A) return true;
  const X = nx * A, Y = ny;
  if (X > A - 0.44 * A && Y > 0.76) return true;          // Broadcast
  if (X < -A + 0.16 * A && Y > 0.76) return true;         // micro
  if (X > A - 0.44 * A && Y < -0.80) return true;         // navigation
  if (Math.abs(X) < 0.4 * A && Y > 0.80) return true;     // nom de la station
  return false;
}
// la caméra d'une vue, à la distance d, regardant l'origine
export function viewCamera(view, d, A, cam) {
  const c = cam || new THREE.PerspectiveCamera(FOV, A, 0.1, 400);
  c.fov = FOV; c.aspect = A;
  const el = view[0] * Math.PI / 180, az = view[1] * Math.PI / 180;
  c.position.set(Math.sin(az) * Math.cos(el) * d, Math.sin(el) * d, Math.cos(az) * Math.cos(el) * d);
  c.up.set(0, 1, 0); c.lookAt(0, 0, 0); c.updateProjectionMatrix(); c.updateMatrixWorld();
  return c;
}
// le cylindre balayé par la grille qui tourne autour de la verticale : ses
// deux cercles (haut et bas), tube compris
export function sweptPoints(dims) {
  const hx = dims[0] / 2, hy = dims[1] / 2, hz = dims[2] / 2;
  // le tube compris sur chaque demi-côté, et le polygone de 96 points agrandi
  // pour CONTENIR le cercle (inscrit, il passait sous les coins)
  const R = Math.hypot(hx + TUBE_R, hz + TUBE_R) / Math.cos(Math.PI / 96), pts = [];
  for (let k = 0; k < 96; k++) { const a = k / 96 * Math.PI * 2; for (const y of [-hy - TUBE_R, hy + TUBE_R]) pts.push([Math.cos(a) * R, y, Math.sin(a) * R]); }
  return pts;
}
export function fitDistance(view, dims, A) {
  const pts = sweptPoints(dims), cam = new THREE.PerspectiveCamera(FOV, A, 0.1, 400);
  const ok = (d) => { viewCamera(view, d, A, cam); for (const p of pts) { _v.set(p[0], p[1], p[2]).project(cam); if (forbidden(_v.x, _v.y, A)) return false; } return true; };
  let lo = 5, hi = 300;
  for (let i = 0; i < 40; i++) { const m = (lo + hi) / 2; if (ok(m)) hi = m; else lo = m; }
  return hi;
}

// --------------------------------------------------------------- les shaders
const COMMON = `
  uniform sampler2D uData;         // 3 rangées : (cellule, code de direction), (naissance, drapeaux, aléa, -), (couleur, tuyau)
  uniform float uCount;
  uniform float uStep;             // l'horloge de pas (continue)
  uniform float uElbow;            // 1 : coudes, 0 : sphères
  uniform mat3 uRot;               // la rotation de la structure
  uniform vec3 uCenter;            // le centre de composition, dans le monde
  uniform vec3 uHalf;              // le demi-gabarit de la grille (centre des cellules -> monde)
  uniform float uExplode;          // temps depuis le drop (-1 : rien)
  uniform float uExplodeBefore;    // l'horloge de pas au drop : ce qui est né avant explose
  uniform float uCutBirth;         // la dissolution : ce qui est né avant s'éteint
  uniform float uCutSpan;
  varying vec3 vN;
  varying vec3 vCol;
  varying float vHead;
  varying float vFade;
  vec4 fetch(float i, float row) { return texture2D(uData, vec2((i + 0.5) / uCount, (row + 0.5) / 3.0)); }
  vec3 dirOf(float code) {
    float ax = floor(code / 2.0 + 0.01), sg = mod(code, 2.0) > 0.5 ? 1.0 : -1.0;
    return ax < 0.5 ? vec3(sg, 0.0, 0.0) : (ax < 1.5 ? vec3(0.0, sg, 0.0) : vec3(0.0, 0.0, sg));
  }
  // deux vecteurs perpendiculaires à d (axes de la grille : exacts)
  void basis(vec3 d, out vec3 u, out vec3 w) {
    u = abs(d.y) > 0.5 ? vec3(1.0, 0.0, 0.0) : vec3(0.0, 1.0, 0.0);
    w = cross(d, u);
  }
  mat3 rotAxis(vec3 a, float t) {
    float c = cos(t), s = sin(t), k = 1.0 - c;
    return mat3(c + a.x * a.x * k, a.y * a.x * k + a.z * s, a.z * a.x * k - a.y * s,
                a.x * a.y * k - a.z * s, c + a.y * a.y * k, a.z * a.y * k + a.x * s,
                a.x * a.z * k + a.y * s, a.y * a.z * k - a.x * s, c + a.z * a.z * k);
  }
  float h11(float n) { return fract(sin(n * 12.9898) * 43758.5453); }
  // la pièce dans le monde : la rotation de la structure, l'explosion
  void place(vec3 p, vec3 n, vec3 pieceC, float birth, float seed) {
    vec3 c0 = pieceC - uHalf;
    vec3 lp = p - pieceC, ln = n;
    vec3 off = vec3(0.0);
    vFade = 1.0;
    if (uExplode >= 0.0 && birth <= uExplodeBefore) {
      // vers l'extérieur de la structure, en tournant, sous la pesanteur
      vec3 outward = normalize(c0 + vec3(1e-3, 2e-3, 3e-3) + 0.35 * (vec3(h11(seed), h11(seed + 1.7), h11(seed + 3.1)) - 0.5));
      float t = uExplode, v = 9.0 + 7.0 * h11(seed + 5.3);
      off = outward * v * t + vec3(0.0, -7.0, 0.0) * t * t;
      mat3 R = rotAxis(normalize(vec3(h11(seed + 7.0), h11(seed + 8.0), h11(seed + 9.0)) - 0.5 + vec3(1e-3)), t * (3.0 + 6.0 * h11(seed + 11.0)));
      lp = R * lp; ln = R * ln;
      vFade = 1.0 - smoothstep(0.4, 1.0, t / ${EXPLODE_BEATS.toFixed(1)});
    }
    // la dissolution : les plus anciennes d'abord
    vFade *= smoothstep(uCutBirth, uCutBirth + uCutSpan, birth);
    vec3 world = uCenter + uRot * (c0 + off + lp * max(vFade, 0.0001));
    vN = normalize(mat3(viewMatrix) * (uRot * ln));
    gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
  }
`;
// le segment : un cylindre unité (y de 0 à 1, rayon 1), de la cellule au centre de la suivante
const SEG_VERTEX = `
  precision highp float;
  attribute float aIndex;
  ${COMMON}
  void main() {
    vec4 a = fetch(aIndex, 0.0), b = fetch(aIndex, 1.0), c = fetch(aIndex, 2.0);
    float birth = b.x;
    if (birth > uStep || c.a < 0.5) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    float flags = b.y;
    float tS = mod(flags, 2.0) > 0.5 ? uElbow * ${ELBOW_R.toFixed(2)} : 0.0;
    float tE = flags > 1.5 ? uElbow * ${ELBOW_R.toFixed(2)} : 0.0;
    // la pousse : la tête avance d'une cellule par pas, sans saut
    float grow = clamp(uStep - birth, 0.0, 1.0);
    float s1 = min(1.0 - tE, grow);
    if (s1 <= tS + 1e-4) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    vec3 d = dirOf(a.w), u, w; basis(d, u, w);
    float s = mix(tS, s1, position.y);
    vec3 p = a.xyz + d * s + (u * position.x + w * position.z) * ${TUBE_R.toFixed(3)};
    vec3 n = u * normal.x + w * normal.z + d * normal.y;
    vCol = c.rgb;
    vHead = grow < 1.0 ? smoothstep(0.0, 0.35, s - (s1 - 0.35)) : 0.0;
    place(p, n, a.xyz + d * 0.5, birth, aIndex * 1.37 + 0.5);
  }
`;
// le joint : une sphère (uElbow 0) ou un coude en quart de tore (uElbow 1)
const JOINT_VERTEX = `
  precision highp float;
  attribute float aIndex;
  attribute float aElbow;          // 1 : ce maillage est le coude, 0 : la sphère
  ${COMMON}
  void main() {
    vec4 a = fetch(aIndex, 0.0), b = fetch(aIndex, 1.0), c = fetch(aIndex, 2.0);
    float birth = b.x, kind = b.y;  // 0 départ, 1 virage, 2 bout
    bool elbow = kind > 0.5 && kind < 1.5 && uElbow > 0.5;
    if (birth > uStep + 0.5 || c.a < 0.5 || (aElbow > 0.5) != elbow) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    vec3 din = dirOf(a.w), dout = dirOf(b.z);
    vec3 p, n;
    if (aElbow > 0.5) {
      // le quart de tore : position.x = angle (0..1), (position.y, position.z) = la section
      float g = clamp((uStep - (birth - ${ELBOW_R.toFixed(2)})) / ${(2 * ELBOW_R).toFixed(2)}, 0.0, 1.0);
      if (g <= 1e-3) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
      float th = position.x * g * 1.5707963;
      vec3 C = a.xyz + (dout - din) * ${ELBOW_R.toFixed(2)};       // le centre de courbure
      vec3 rad = -dout * cos(th) + din * sin(th);                  // de C vers l'axe du tube
      vec3 tng = dout * sin(th) + din * cos(th);
      vec3 bin = cross(tng, rad);
      vec3 sec = rad * position.y + bin * position.z;
      p = C + rad * ${ELBOW_R.toFixed(2)} + sec * ${TUBE_R.toFixed(3)};
      n = sec;
      vHead = g < 1.0 ? 1.0 : 0.0;
    } else {
      // la sphère : elle grandit quand la tête arrive
      float k = kind < 0.5 ? smoothstep(birth, birth + 0.3, uStep) : smoothstep(birth - 0.12, birth, uStep);
      p = a.xyz + position * ${JOINT_R.toFixed(3)} * max(k, 1e-4);
      n = normal;
      vHead = 0.0;
    }
    vCol = c.rgb;
    place(p, n, a.xyz, birth, aIndex * 2.11 + 100.0);
  }
`;
const FRAGMENT = `
  precision highp float;
  uniform sampler2D uMat;
  uniform float uHeadGain;
  uniform float uGain;
  varying vec3 vN;
  varying vec3 vCol;
  varying float vHead;
  varying float vFade;
  void main() {
    vec3 n = normalize(vN);
    if (!gl_FrontFacing) n = -n;
    vec4 m = texture2D(uMat, n.xy * 0.49 + 0.5);
    float body = m.b * m.b * ${MATCAP_GAIN.toFixed(2)};
    // néon : la couleur très saturée suit le studio (zones sombres), le reflet
    // reste blanc et net, le coeur s'éclaire face à la caméra (tube éclairé
    // de l'intérieur)
    float core = pow(max(n.z, 0.0), 3.0);
    // (le bloom à 0,42 voilait le fond de gris ; un corps en body² rendait le
    // néon terne : olive, prune)
    vec3 col = vCol * (0.02 + 1.3 * body) + vec3(1.0) * m.a * ${(CHROME_BOX * 0.8).toFixed(2)} + vCol * 0.45 * core;
    col += vCol * vHead * uHeadGain;
    gl_FragColor = vec4(col * vFade * uGain, 1.0);
  }
`;
// la lueur des têtes : un quad face caméra, halo en pixels nul au bord (R12)
const GLOW_VERTEX = `
  precision highp float;
  attribute float aIndex;
  uniform vec4 uHead[8];
  uniform vec3 uHeadCol[8];
  uniform mat3 uRot;
  uniform vec3 uCenter;
  uniform vec3 uHalf;
  varying vec2 vQ;
  varying vec3 vCol;
  void main() {
    int i = int(aIndex + 0.5);
    vec4 h = uHead[0]; vec3 c = uHeadCol[0];
    ${Array.from({ length: 8 }, (_, k) => `if (i == ${k}) { h = uHead[${k}]; c = uHeadCol[${k}]; }`).join('\n    ')}
    if (h.w <= 0.0) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    vec3 world = uCenter + uRot * (h.xyz - uHalf);
    vec4 v = viewMatrix * vec4(world, 1.0);
    v.xy += position.xy * 1.6;                       // 1,6 cellule de rayon
    vQ = position.xy; vCol = c * h.w;
    gl_Position = projectionMatrix * v;
  }
`;
const GLOW_FRAGMENT = `
  precision mediump float;
  varying vec2 vQ;
  varying vec3 vCol;
  void main() {
    float r = length(vQ);
    // exp(-r / 0,16) : à 6 largeurs de halo (r = 0,96), déjà nul (R12)
    float a = exp(-r / 0.16) * (1.0 - smoothstep(0.55, 0.95, r)) * 0.22;
    gl_FragColor = vec4(vCol * a, 1.0);
  }
`;
const DISPLAY_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const DISPLAY_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uTex, vUv).rgb, 1.0); }
`;

// la géométrie : un cylindre unité fermé (y 0..1, normales exactes), une
// sphère, un quart de tore paramétré (x = angle 0..1, y/z = la section)
function cylinderGeometry(radial) {
  const g = new THREE.CylinderGeometry(1, 1, 1, radial, 1, false);
  g.translate(0, 0.5, 0);
  return g;
}
function elbowGeometry(radial, steps) {
  const pos = [], nrm = [], idx = [];
  for (let i = 0; i <= steps; i++) for (let j = 0; j <= radial; j++) {
    const a = j / radial * Math.PI * 2;
    pos.push(i / steps, Math.cos(a), Math.sin(a)); nrm.push(0, Math.cos(a), Math.sin(a));
  }
  for (let i = 0; i < steps; i++) for (let j = 0; j < radial; j++) {
    const p0 = i * (radial + 1) + j, p1 = p0 + 1, p2 = p0 + radial + 1, p3 = p2 + 1;
    idx.push(p0, p2, p1, p1, p2, p3);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  g.setIndex(idx);
  return g;
}
function instanced(base, count, extra) {
  const g = new THREE.InstancedBufferGeometry();
  g.setAttribute('position', base.getAttribute('position'));
  g.setAttribute('normal', base.getAttribute('normal'));
  g.setIndex(base.getIndex());
  const idx = new Float32Array(count); for (let i = 0; i < count; i++) idx[i] = i;
  g.setAttribute('aIndex', new THREE.InstancedBufferAttribute(idx, 1));
  if (extra != null) g.setAttribute('aElbow', new THREE.InstancedBufferAttribute(new Float32Array(count).fill(extra), 1));
  g.instanceCount = 0;
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
  return g;
}

let MATCAP = null;
function matcapTexture() {
  if (MATCAP) return MATCAP;
  const m = genererCanauxRuban(256);
  const t = new THREE.DataTexture(m.data, m.size, m.size, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.colorSpace = THREE.NoColorSpace; t.minFilter = t.magFilter = THREE.LinearFilter; t.generateMipmaps = false; t.needsUpdate = true;
  MATCAP = t;
  return t;
}
function dataTexture(count) {
  const data = new Float32Array(count * 3 * 4);
  const t = new THREE.DataTexture(data, count, 3, THREE.RGBAFormat, THREE.FloatType);
  t.minFilter = t.magFilter = THREE.NearestFilter; t.generateMipmaps = false; t.needsUpdate = true;
  return { data, tex: t };
}

// =============================================================================

export class PipesVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 2;
    this._L = TIER[this._tier];
    const j = (urlParam('pipesjoint') || '').toLowerCase();
    this._forceJoint = j === 'sphere' ? 0 : (j === 'elbow' ? 1 : null);
    const f = parseFloat(urlParam('pipesfill'));
    this._forceFill = Number.isFinite(f) ? clamp01(f) : null;
    this._forceExplode = urlParam('pipesexplode') === '1';
    this._post = new VdjPost({ bloomStrength: 0.18, bloomThreshold: 0.86, grain: 0.0, vignette: 0.35, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
    if (urlParam('pipesprobe') === '1' && typeof window !== 'undefined') window.__pipes = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._prevBackground = scene.background;
    scene.background = new THREE.Color(0x000000);
    const L = this._L;
    this._seg = dataTexture(L.segs);
    this._jnt = dataTexture(L.joints);
    this._gain = { value: 1 };
    const shared = {
      uStep: { value: 0 }, uElbow: { value: 0 }, uRot: { value: new THREE.Matrix3() }, uCenter: { value: new THREE.Vector3() },
      uHalf: { value: new THREE.Vector3((L.grid[0] - 1) / 2, (L.grid[1] - 1) / 2, (L.grid[2] - 1) / 2) },
      uExplode: { value: -1 }, uExplodeBefore: { value: -1e9 }, uCutBirth: { value: -1e9 }, uCutSpan: { value: 1 },
      uMat: { value: matcapTexture() }, uHeadGain: { value: 0.5 }, uGain: this._gain
    };
    this._U = shared;
    const mat = (vs, data, count) => new THREE.ShaderMaterial({
      uniforms: Object.assign({}, shared, { uData: { value: data.tex }, uCount: { value: count } }),
      vertexShader: vs, fragmentShader: FRAGMENT, side: THREE.DoubleSide
    });
    this._segGeo = instanced(cylinderGeometry(L.radial), L.segs);
    this._segMat = mat(SEG_VERTEX, this._seg, L.segs);
    this._segMesh = new THREE.Mesh(this._segGeo, this._segMat);
    const sph = new THREE.SphereGeometry(1, L.radial, Math.max(6, L.radial / 2));
    this._sphGeo = instanced(sph, L.joints, 0);
    this._elbGeo = instanced(elbowGeometry(L.radial, 8), L.joints, 1);
    this._jntMat = mat(JOINT_VERTEX, this._jnt, L.joints);
    this._sphMesh = new THREE.Mesh(this._sphGeo, this._jntMat);
    this._elbMesh = new THREE.Mesh(this._elbGeo, this._jntMat);
    // la lueur des têtes
    this._GU = { uHead: { value: Array.from({ length: 8 }, () => new THREE.Vector4()) }, uHeadCol: { value: Array.from({ length: 8 }, () => new THREE.Vector3()) }, uRot: shared.uRot, uCenter: shared.uCenter, uHalf: shared.uHalf };
    const quad = new THREE.PlaneGeometry(2, 2);
    this._glowGeo = instanced(quad, 8); this._glowGeo.instanceCount = 8;
    this._glowMesh = new THREE.Mesh(this._glowGeo, new THREE.ShaderMaterial({ uniforms: this._GU, vertexShader: GLOW_VERTEX, fragmentShader: GLOW_FRAGMENT, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
    for (const m of [this._segMesh, this._sphMesh, this._elbMesh, this._glowMesh]) m.frustumCulled = false;
    this._glowMesh.renderOrder = 5;

    // MSAA 4x : les tubes dans une scène privée, affichée par un quad
    this._private = new THREE.Scene();
    this._private.add(this._segMesh, this._sphMesh, this._elbMesh, this._glowMesh);
    this._msaaTarget = null;
    this._dispU = { uTex: { value: null } };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._dispU, vertexShader: DISPLAY_VERTEX, fragmentShader: DISPLAY_FRAGMENT, depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer, sc, cam) { self._renderPrivate(renderer, cam); };
    scene.add(this._display);

    // l'état
    this._r = rng(9001);
    this._time = 0; this._clock = 0; this._clockPrev = 0; this._targetOffset = null;
    this._beatCount = 0; this._barCount = 0; this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._bpm = NO_BPM; this._step = 0; this._stepInt = 0; this._buildup = 0;
    this._view = 0; this._joint = this._forceJoint != null ? this._forceJoint : 0; this._colourIdx = 0;
    this._accent = null; this._paletteKey = null;
    this._drop = null; this._dissolve = null; this._lastMode = null;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._fit = new Map();
    this._log = [];
    this._stats = { steps: 0, spawned: 0, drops: 0, dissolves: 0, maxFill: 0 };
    this._reset(true);
    if (this._forceFill != null) this._prefill(this._forceFill);
  }

  // ------------------------------------------------------------------ la pousse
  _newColour() {
    const k = this._colourIdx++;
    // une couleur sur quatre : l'accent de la pochette ; sinon la roue néon
    const hex = this._accent != null && k % 4 === 3 ? this._accent : NEON[k % NEON.length];
    return vdjColor(hex, new THREE.Color(), 0.92);
  }
  _reset(centre) {
    this._grid = new PipeGrid(this._L.grid, this._r);
    this._segN = 0; this._jntN = 0;
    this._segGeo.instanceCount = 0; this._sphGeo.instanceCount = 0; this._elbGeo.instanceCount = 0;
    this._U.uExplode.value = -1; this._U.uExplodeBefore.value = -1e9; this._U.uCutBirth.value = -1e9;
    const n = Math.max(1, this._L.pipes - 1);
    for (let k = 0; k < n; k++) this._spawn(centre);
  }
  _spawn(centre) {
    const c = this._newColour();
    const p = this._grid.spawn(this._stepInt, [c.r, c.g, c.b], centre);
    if (!p) return null;
    this._stats.spawned++;
    this._writeJoints();
    return p;
  }
  // les textures : seulement les pièces nouvelles
  _writeSegments() {
    const G = this._grid, D = this._seg.data, M = this._L.segs;
    let dirty = false;
    while (this._segN < G.segments.length && this._segN < M) {
      const s = G.segments[this._segN], i = this._segN;
      const code = s.dir[0] ? (s.dir[0] > 0 ? 1 : 0) : s.dir[1] ? (s.dir[1] > 0 ? 3 : 2) : (s.dir[2] > 0 ? 5 : 4);
      D.set([s.from[0], s.from[1], s.from[2], code], i * 4);
      D.set([s.birth, (s.turnStart ? 1 : 0) + (s.turnEnd ? 2 : 0), 0, 0], (M + i) * 4);
      D.set([s.colour[0], s.colour[1], s.colour[2], 1], (2 * M + i) * 4);
      this._segN++; dirty = true;
    }
    if (dirty) { this._seg.tex.needsUpdate = true; this._segGeo.instanceCount = this._segN; }
  }
  _writeJoints() {
    const G = this._grid, D = this._jnt.data, M = this._L.joints;
    let dirty = false;
    while (this._jntN < G.joints.length && this._jntN < M) {
      const j = G.joints[this._jntN], i = this._jntN;
      const code = (d) => d[0] ? (d[0] > 0 ? 1 : 0) : d[1] ? (d[1] > 0 ? 3 : 2) : (d[2] > 0 ? 5 : 4);
      D.set([j.cell[0], j.cell[1], j.cell[2], code(j.din)], i * 4);
      D.set([j.birth, j.kind === 'start' ? 0 : (j.kind === 'turn' ? 1 : 2), code(j.dout), 0], (M + i) * 4);
      D.set([j.colour[0], j.colour[1], j.colour[2], 1], (2 * M + i) * 4);
      this._jntN++; dirty = true;
    }
    if (dirty) { this._jnt.tex.needsUpdate = true; this._sphGeo.instanceCount = this._jntN; this._elbGeo.instanceCount = this._jntN; }
  }
  // un pas de pousse (une croche, une double croche en montée)
  _advance(stepInt) {
    const G = this._grid;
    for (const p of G.pipes) if (p.alive) { G.step(p, stepInt); this._stats.steps++; }
    // les tuyaux bloqués sont remplacés ; jamais moins de 3 vivants (tier 0 : le compte du tier)
    const alive = G.pipes.filter((p) => p.alive).length;
    const want = Math.min(this._L.pipes, 3);
    for (let k = alive; k < want; k++) if (!this._spawn(false)) break;
    this._writeSegments(); this._writeJoints();
    this._stats.maxFill = Math.max(this._stats.maxFill, G.fill);
    // la mémoire des instances est pleine : on recommence comme à 60 %
    if (!this._dissolve && !this._drop && (G.fill >= FILL_DISSOLVE || this._segN >= this._L.segs - 8 || this._jntN >= this._L.joints - 8)) this._startDissolve();
  }
  _prefill(f) {
    let k = 0;
    while (this._grid.fill < f && k++ < 4000) { this._stepInt++; this._advance(this._stepInt); if (this._dissolve) { this._dissolve = null; this._U.uCutBirth.value = -1e9; break; } }
    this._step = this._stepInt + 1;
  }
  _startDissolve() {
    const G = this._grid;
    const b0 = G.segments.length ? G.segments[0].birth : this._step, b1 = this._stepInt;
    this._dissolve = { start: this._clock, b0, b1 };
    this._stats.dissolves++;
    this._log.push({ what: 'dissolution', fill: +G.fill.toFixed(2) });
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._drops.sort((a, b) => a - b); this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0; while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) { if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true; this._dropIdx++; }
    return fired;
  }

  _applyPalette(pal) {
    const v = pal && (pal.vividColors || pal.dominantColors);
    const key = v && v.length ? String(v[0]) : '';
    if (key === this._paletteKey) return;
    this._paletteKey = key;
    this._accent = v && v.length ? v[0] : null;
  }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    this._applyPalette(profile.artworkPalette);
    this._place();
    if (profile.playbackState !== 'PLAYING') return;   // pause : tout gèle, la séparation RGB aussi
    this._time += dt;

    // l'horloge, en temps (Domino, Twist)
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase;
    const hasGrid = bp != null && bpm > 0;
    this._bpm = hasGrid ? THREE.MathUtils.clamp(bpm, 60, 200) : NO_BPM;
    this._clockPrev = this._clock;
    if (hasGrid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) this._beatCount++;
      this._phaseBeat = bp;
      let target;
      if (barIn != null) {
        if (this._phaseBar != null && barIn < this._phaseBar - 0.5) this._barCount++;
        this._phaseBar = barIn;
        target = 4 * (this._barCount + barIn);
      } else target = this._beatCount + bp;
      if (this._targetOffset == null) this._targetOffset = 4 * Math.round((this._clock - target) / 4);
      const err = target + this._targetOffset - this._clock;
      if (Math.abs(err) > 2) { this._targetOffset = 4 * Math.round((this._clock - target) / 4); this._clock = target + this._targetOffset; }
      else { const rate = this._bpm / 60; this._clock += Math.max(dt * rate * 0.5, dt * rate + err * Math.min(1, dt / 0.5)); }
    } else { this._clock += dt * this._bpm / 60; this._targetOffset = null; }
    const clock = this._clock, dBeat = Math.max(0, clock - this._clockPrev);
    const bar = Math.floor(clock / 4) !== Math.floor(this._clockPrev / 4);
    let phrase = false;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else phrase = Math.floor(clock / 16) !== Math.floor(this._clockPrev / 16);

    // la montée : double croche
    const director = profile.director;
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._buildup += (build - this._buildup) * (1 - Math.exp(-dt / 0.8));
    const rate = this._buildup > 0.5 ? 4 : 2;           // pas par temps

    // DROP
    let drop = hasGrid && this._tickDrops(profile);
    if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid) drop = true;
    this._lastMode = director ? director.mode : null;
    if (this._forceExplode && bar && Math.floor(clock / 4) % 8 === 7) drop = true;
    if (drop && !this._drop) {
      this._drop = { start: clock };
      this._dissolve = null; this._U.uCutBirth.value = -1e9;
      this._U.uExplodeBefore.value = this._step + 1;
      if (this._comfort.requestFlash(this._time)) this._flash = 1;
      this._stats.drops++;
      this._log.push({ what: 'drop', beat: +clock.toFixed(2), segments: this._segN });
    }

    // l'horloge de pas : continue ; un pas entier = une avance de la grille
    if (!this._drop && !this._dissolve) {
      this._step += dBeat * rate;
      while (Math.floor(this._step) > this._stepInt) { this._stepInt++; this._advance(this._stepInt); }
    }
    this._U.uStep.value = this._step;

    // MESURE : un nouveau tuyau (3 à 5 vivants ; 8 en montée)
    if (bar && !this._drop && !this._dissolve) {
      const alive = this._grid.pipes.filter((p) => p.alive).length;
      const cap = this._buildup > 0.5 ? this._L.build : this._L.pipes;
      if (alive < cap) this._spawn(false);
    }
    // PHRASE : coupe - nouvel angle de vue, nouveau style de joint
    if (phrase) {
      this._view = (this._view + 1) % VIEWS.length;
      if (this._forceJoint == null) this._joint = 1 - this._joint;
    }
    this._U.uElbow.value = this._joint;

    // l'explosion, puis la grille vide et la pousse repart du centre
    if (this._drop) {
      const t = clock - this._drop.start;
      this._U.uExplode.value = t;
      if (t >= EXPLODE_BEATS) { this._drop = null; this._reset(true); }
    }
    // la dissolution : des plus anciens aux plus récents, en 2 temps
    if (this._dissolve) {
      const u = clamp01((clock - this._dissolve.start) / DISSOLVE_BEATS);
      const { b0, b1 } = this._dissolve;
      this._U.uCutSpan.value = Math.max(1, (b1 - b0) * 0.15);
      this._U.uCutBirth.value = b0 - this._U.uCutSpan.value + (b1 - b0 + 1 + this._U.uCutSpan.value) * u;
      if (u >= 1) { this._dissolve = null; this._reset(true); }
    }

    // les têtes : position continue, pulsation sur le temps
    const beatPhase = clock - Math.floor(clock);
    const pulse = 1 + 0.8 * Math.exp(-beatPhase * 6);
    this._U.uHeadGain.value = 0.35 * pulse;
    const H = this._GU.uHead.value, HC = this._GU.uHeadCol.value;
    let k = 0;
    const frac = this._step - this._stepInt;
    for (const p of this._grid.pipes) {
      if (!p.growFrom || k >= 8 || this._drop || this._dissolve || p.growBirth < this._stepInt) continue;
      // la tête : le bout du segment qui pousse, à la fraction du pas
      const a = p.growFrom, d = p.growDir;
      H[k].set(a[0] + d[0] * frac, a[1] + d[1] * frac, a[2] + d[2] * frac, 0.55 * pulse);
      HC[k].set(p.colour[0], p.colour[1], p.colour[2]);
      k++;
    }
    for (; k < 8; k++) H[k].w = 0;

    // R10 : la luminance moyenne estimée (remplissage, flash)
    this._flash = (this._flash || 0) * Math.exp(-dt / 0.12);
    const est = 0.03 + 0.18 * this._grid.fill + 0.25 * this._flash;
    this._gain.value = this._comfort.measure(est, dt) * (1 + 0.6 * this._flash);
    this._post.update(vdjBeatChroma(beatPhase, 60 / this._bpm));
  }

  // la place : la rotation (un tour en 32 temps), la caméra de la vue, le
  // centre de composition (R19)
  _place() {
    const cam = this._camera;
    const A = cam.aspect || 16 / 9;
    const key = this._view + '|' + A.toFixed(3);
    if (!this._fit.has(key)) this._fit.set(key, fitDistance(VIEWS[this._view], this._L.grid, A));
    const d = this._fit.get(key);
    viewCamera(VIEWS[this._view], d, A, cam);
    cam.near = 0.5; cam.far = d * 3; cam.updateProjectionMatrix();
    stageWorldAt(cam, d, this._U.uCenter.value);
    const ang = (this._clock / TURN_BEATS) * Math.PI * 2;
    this._U.uRot.value.setFromMatrix4(new THREE.Matrix4().makeRotationY(ang));
    this._dist = d;
  }

  _renderPrivate(renderer, camera) {
    const cur = renderer.getRenderTarget();
    let w, h;
    if (cur) { w = cur.width; h = cur.height; } else { const b = renderer.getDrawingBufferSize(_size); w = b.x; h = b.y; }
    if (!this._msaaTarget || this._msaaTarget.width !== w || this._msaaTarget.height !== h) {
      if (this._msaaTarget) this._msaaTarget.dispose();
      this._msaaTarget = new THREE.WebGLRenderTarget(w, h, { samples: 4, depthBuffer: true, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    }
    const cc = renderer.getClearColor(_cc), ca = renderer.getClearAlpha();
    renderer.setRenderTarget(this._msaaTarget);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, false);
    renderer.render(this._private, camera);
    renderer.setRenderTarget(cur);
    renderer.setClearColor(cc, ca);
    this._dispU.uTex.value = this._msaaTarget.texture;
  }

  getProbe() {
    const G = this._grid;
    return { tier: this._tier, step: +this._step.toFixed(3), segments: this._segN, joints: this._jntN, fill: +G.fill.toFixed(3), alive: G.pipes.filter((p) => p.alive).length,
      view: this._view, joint: this._joint, drop: !!this._drop, dissolve: !!this._dissolve, dist: this._dist, stats: Object.assign({}, this._stats), log: this._log.slice(-6) };
  }

  dispose() {
    if (this._scene) {
      if (this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); }
      this._scene.background = this._prevBackground || null;
    }
    for (const m of [this._display, this._segMesh, this._sphMesh, this._glowMesh]) if (m) { m.geometry.dispose(); m.material.dispose(); }
    if (this._elbGeo) this._elbGeo.dispose();
    if (this._msaaTarget) this._msaaTarget.dispose();
    if (this._seg) this._seg.tex.dispose();
    if (this._jnt) this._jnt.tex.dispose();
    if (typeof window !== 'undefined' && window.__pipes === this) window.__pipes = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _cc = new THREE.Color();
